import { apiFetch } from './http';
import { REQUEST_OPERATION_HEADER } from '../lib/actionFailureSignals';

export class ServiceTicketApiError extends Error {
  code: string | null;
  status: number;

  constructor(message: string, options: { code?: string | null; status: number }) {
    super(message);
    this.name = 'ServiceTicketApiError';
    this.code = options.code ?? null;
    this.status = options.status;
  }
}

async function readServiceTicketApiError(res: Response, fallback: string): Promise<{ message: string; code: string | null }> {
  const text = await res.text();
  if (!text.trim()) return { message: fallback, code: null };
  try {
    const data = JSON.parse(text) as { error?: unknown; detail?: unknown; message?: unknown };
    for (const value of [data.message, data.detail, data.error]) {
      if (typeof value === 'string' && value.trim()) {
        return {
          message: value,
          code: typeof data.error === 'string' && data.error.trim() ? data.error : null,
        };
      }
    }
  } catch {
    // Keep the raw text fallback for non-JSON server responses.
  }
  return { message: text, code: null };
}

export type ServiceTicketLogisticsDistanceSnapshot = {
  distanceMiles: number | null;
  driveMinutes: number | null;
  travelHours: number | null;
  source: string | null;
  computedAt: string | null;
  routeKey: string | null;
  cached: boolean;
  travelCharge: {
    miles?: number | null;
    baseLineItemId?: string | null;
    mileageLineItemId?: string | null;
    baseChargeCode?: string | null;
    mileageChargeCode?: string | null;
    excessMiles?: number | null;
    customQuoteRequired?: boolean | null;
    travelChargeTotal?: number | null;
  } | null;
};

export async function refreshServiceTicketLogisticsDistance(
  ticketId: string,
  options?: { force?: boolean },
): Promise<ServiceTicketLogisticsDistanceSnapshot> {
  const res = await apiFetch(`/service-tickets/${ticketId}/logistics-distance/refresh`, {
    method: 'POST',
    body: JSON.stringify({
      force: options?.force === true,
    }),
  });
  if (!res.ok) {
    const error = await readServiceTicketApiError(res, 'Failed to refresh service ticket route distance');
    throw new ServiceTicketApiError(error.message, { code: error.code, status: res.status });
  }
  const data = (await res.json()) as {
    distanceMiles?: number | null;
    driveMinutes?: number | null;
    travelHours?: number | null;
    source?: string | null;
    computedAt?: string | null;
    routeKey?: string | null;
    cached?: boolean;
    travelCharge?: ServiceTicketLogisticsDistanceSnapshot['travelCharge'];
  };
  return {
    distanceMiles: data.distanceMiles ?? null,
    driveMinutes: data.driveMinutes ?? null,
    travelHours: data.travelHours ?? null,
    source: data.source ?? null,
    computedAt: data.computedAt ?? null,
    routeKey: data.routeKey ?? null,
    cached: Boolean(data.cached),
    travelCharge: data.travelCharge ?? null,
  };
}

export type ServiceEstimatePoint = { latitude: number; longitude: number };

export type ServiceEstimateRoute = {
  origin: {
    name: string | null;
    address: string;
    postalCode: string | null;
    /** Null until Google has placed the service location. */
    point: ServiceEstimatePoint | null;
  };
  route: {
    distanceMiles: number;
    driveMinutes: number;
    destination: ServiceEstimatePoint | null;
  } | null;
};

function estimatePoint(value: { latitude?: unknown; longitude?: unknown } | null | undefined): ServiceEstimatePoint | null {
  const latitude = Number(value?.latitude);
  const longitude = Number(value?.longitude);
  return value?.latitude != null && Number.isFinite(latitude) && Number.isFinite(longitude) ? { latitude, longitude } : null;
}

/**
 * Google's drive from the service location to an address, a ZIP or a map point. With no destination it only places
 * the service location. Nothing is saved.
 */
export async function fetchServiceEstimateRoute(
  destination?: { address: string } | ServiceEstimatePoint,
): Promise<ServiceEstimateRoute> {
  const body = !destination ? {} : 'address' in destination
    ? { destination: destination.address }
    : { latitude: destination.latitude, longitude: destination.longitude };
  // A POST only to carry the destination: marked as a read, so a failure stays in the dialog and a success
  // doesn't count as a saved change.
  const res = await apiFetch('/service-estimate/route', {
    method: 'POST',
    headers: { [REQUEST_OPERATION_HEADER]: 'read' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const error = await readServiceTicketApiError(res, 'The drive could not be measured.');
    throw new ServiceTicketApiError(error.message, { code: error.code, status: res.status });
  }
  const data = (await res.json()) as {
    origin?: { name?: string | null; address?: string; postalCode?: string | null; latitude?: number; longitude?: number };
    route?: { distanceMiles?: number; driveMinutes?: number; destination?: { latitude?: number; longitude?: number } | null } | null;
  };
  const miles = Number(data.route?.distanceMiles);
  return {
    origin: {
      name: data.origin?.name ?? null,
      address: data.origin?.address ?? '',
      postalCode: data.origin?.postalCode ?? null,
      point: estimatePoint(data.origin),
    },
    route: data.route && Number.isFinite(miles)
      ? { distanceMiles: miles, driveMinutes: Number(data.route.driveMinutes) || 0, destination: estimatePoint(data.route.destination) }
      : null,
  };
}
